import { useState, useEffect, useCallback } from "react";
import {
  FiSearch, FiX, FiCheck, FiLock, FiPrinter, FiTrash2,
  FiMinus, FiPlus, FiCalendar, FiUser, FiChevronRight, FiEdit2, FiAlertTriangle, FiTruck,
} from "react-icons/fi";
import { apiGet, apiPost } from "../api";
import { Btn, Pill, Icon, fmtInvNo, Pagination, PER_PAGE_DEFAULT } from "./KioskUI";

/* ─── helpers ─── */
const MMK = (n) => Number(n).toLocaleString("en-US", { maximumFractionDigits: 0 });
const initials = (name) => (name || "?").split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0]).join("").toUpperCase() || "?";
const today = () => new Date().toISOString().split("T")[0];

const numOnly = (s) => (s.match(/\d/g) || []).join("");

/* ─── small style tokens ─── */
const lbl = { display: "block", fontSize: 11.5, fontWeight: 700, color: "var(--sub)", letterSpacing: ".02em", marginBottom: 6 };
const fieldBox = { display: "flex", alignItems: "center", gap: 8, border: "1px solid var(--line)", borderRadius: "var(--r-sm)", padding: "9px 12px", background: "var(--surface)" };
const sqbtn = { width: 30, height: 30, borderRadius: 8, border: "1px solid var(--line)", background: "var(--surface)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 };

/* ─── Overlay ─── */
function Overlay({ children, onClose }) {
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(19,24,32,.42)", backdropFilter: "blur(2px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 50 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: "var(--surface)", borderRadius: "var(--r-xl)", boxShadow: "0 24px 70px rgba(19,24,32,.3)" }}>
        {children}
      </div>
    </div>
  );
}

/* ─── SaleRow ─── */
function SaleRow({ k, v, accent }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13.5, marginBottom: 7 }}>
      <span style={{ color: "var(--sub)" }}>{k}</span>
      <span style={{ fontWeight: 600, color: accent ? "var(--accent)" : "var(--ink)", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>{v}</span>
    </div>
  );
}

/* ─── Manager PIN modal ─── */
function PinModal({ token, onClose, onVerified }) {
  const [pin, setPin]   = useState("");
  const [err, setErr]   = useState("");
  const [busy, setBusy] = useState(false);
  const keys = ["1","2","3","4","5","6","7","8","9","clear","0","back"];

  const press = (k) => {
    setErr("");
    if (k === "clear") return setPin("");
    if (k === "back")  return setPin(p => p.slice(0, -1));
    setPin(p => p.length < 6 ? p + k : p);
  };

  const submit = async () => {
    if (!pin) return;
    setBusy(true);
    try {
      const res = await apiPost("/api/inventory/manager/verify-pin", { employee_token: token, manager_pin: pin }, token);
      if (res.ok) {
        const data = await res.json();
        onVerified(data.manager_name || "Manager", data.manager_id);
      } else {
        setErr("Invalid PIN — try again");
        setPin("");
      }
    } catch { setErr("Cannot connect to server."); }
    finally { setBusy(false); }
  };

  const dots = [0,1,2,3,4,5].map(i => i < pin.length);

  return (
    <Overlay onClose={onClose}>
      <div style={{ width: 360, padding: "26px 26px 24px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 11, marginBottom: 4 }}>
          <div style={{ width: 38, height: 38, borderRadius: 11, background: "var(--warning-bg)", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <FiLock size={19} color="var(--warning)" />
          </div>
          <div>
            <div style={{ fontFamily: "var(--font-head)", fontSize: 17, fontWeight: 800, color: "var(--ink)" }}>Manager approval</div>
            <div style={{ fontSize: 12.5, color: "var(--sub)" }}>Discounts need a manager or owner PIN</div>
          </div>
        </div>

        <div style={{ display: "flex", gap: 11, justifyContent: "center", margin: "22px 0" }}>
          {dots.map((f, i) => (
            <span key={i} style={{ width: f ? 14 : 11, height: f ? 14 : 11, borderRadius: 99, background: f ? (err ? "var(--danger)" : "var(--primary)") : "transparent", border: f ? "none" : `2px solid ${err ? "var(--danger)" : "var(--n-300)"}`, transition: "all .12s" }} />
          ))}
        </div>
        {err && <div style={{ textAlign: "center", color: "var(--danger)", fontSize: 13, fontWeight: 600, marginBottom: 14, marginTop: -8 }}>{err}</div>}

        <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 9, marginBottom: 16 }}>
          {keys.map(k => {
            const soft = k === "clear" || k === "back";
            return (
              <button key={k} onClick={() => press(k)} style={{ height: 54, borderRadius: 11, cursor: "pointer", background: soft ? "transparent" : "var(--surface)", border: soft ? "none" : "1px solid var(--line)", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "var(--font-head)", fontSize: 22, fontWeight: 700, color: "var(--ink)", boxShadow: soft ? "none" : "var(--shadow-sm)" }}>
                {k === "clear" ? <span style={{ fontFamily: "var(--font-body)", fontSize: 13, fontWeight: 600, color: "var(--sub)" }}>Clear</span>
                  : k === "back" ? <Icon name="backspace" size={20} stroke="var(--sub)" />
                  : k}
              </button>
            );
          })}
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <Btn kind="ghost" onClick={onClose} style={{ flex: 1, justifyContent: "center" }}>Cancel</Btn>
          <Btn kind="pri" onClick={submit} disabled={!pin || busy} style={{ flex: 1, justifyContent: "center" }}>
            <FiCheck size={16} /> Approve
          </Btn>
        </div>
      </div>
    </Overlay>
  );
}

/* ─── Success modal ─── */
function SuccessModal({ saleId, total, pay, itemCount, location, onPrint, onNew, go }) {
  return (
    <Overlay onClose={() => go("overview")}>
      <div style={{ width: 400, padding: "30px 28px 24px", textAlign: "center" }}>
        <div style={{ width: 62, height: 62, borderRadius: 99, background: "var(--success-bg)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 18px" }}>
          <FiCheck size={30} color="var(--success)" strokeWidth={2.4} />
        </div>
        <div style={{ fontFamily: "var(--font-head)", fontSize: 22, fontWeight: 800, color: "var(--ink)" }}>Sale complete</div>
        <div style={{ fontSize: 14, color: "var(--sub)", marginTop: 6 }}>Stock updated · movement logged</div>

        <div style={{ background: "var(--faint)", border: "1px solid var(--line)", borderRadius: "var(--r-md)", padding: "16px 18px", margin: "20px 0", textAlign: "left" }}>
          <SaleRow k="Invoice" v={fmtInvNo(saleId)} />
          <SaleRow k="Location" v={location?.name || "—"} />
          <SaleRow k="Items" v={itemCount} />
          <SaleRow k="Payment" v={pay === "cash" ? "Cash" : "Credit"} />
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginTop: 8, paddingTop: 10, borderTop: "1px solid var(--line)" }}>
            <span style={{ fontFamily: "var(--font-head)", fontSize: 14, fontWeight: 700 }}>Total</span>
            <span style={{ fontFamily: "var(--font-head)", fontSize: 20, fontWeight: 800, fontVariantNumeric: "tabular-nums" }}>{MMK(total)} <span style={{ fontSize: 12, color: "var(--sub)" }}>MMK</span></span>
          </div>
        </div>

        <Btn kind="pri" onClick={onPrint} style={{ width: "100%", justifyContent: "center", height: 50, fontSize: 15.5 }}>
          <FiPrinter size={18} /> Print Invoice
        </Btn>
        <div style={{ display: "flex", gap: 10, marginTop: 10 }}>
          <Btn kind="ghost" onClick={onNew} style={{ flex: 1, justifyContent: "center" }}>New Sale</Btn>
          <Btn kind="soft" onClick={() => go("overview")} style={{ flex: 1, justifyContent: "center" }}>Done</Btn>
        </div>
      </div>
    </Overlay>
  );
}

/* ─── Step 1: Customer picker ─── */
function CustomerStart({ customers, onPick, onCancel }) {
  const [q, setQ] = useState("");
  const matches = customers.filter(c => {
    const s = q.trim().toLowerCase();
    return !s || c.name.toLowerCase().includes(s) || (c.phone || "").replace(/\s/g, "").includes(s.replace(/\s/g, ""));
  });

  return (
    <Overlay onClose={onCancel}>
      <div style={{ width: 484, padding: "24px 26px 22px", display: "flex", flexDirection: "column", maxHeight: "84vh" }}>
        {/* header */}
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 18 }}>
          <div>
            <div style={{ display: "flex", alignItems: "baseline", gap: 9 }}>
              <span style={{ fontFamily: "var(--font-head)", fontSize: 20, fontWeight: 800, color: "var(--ink)", letterSpacing: "-.02em" }}>New Sale</span>
              <span style={{ fontFamily: "var(--font-mm)", fontSize: 14, color: "var(--n-400)" }}>အရောင်း</span>
            </div>
            <div style={{ fontSize: 13, color: "var(--sub)", marginTop: 4 }}>Step 1 of 2 · Choose a customer to begin</div>
          </div>
          <button onClick={onCancel} style={{ border: "1px solid var(--line)", background: "var(--surface)", borderRadius: "var(--r-sm)", padding: 7, cursor: "pointer", display: "flex" }}>
            <FiX size={16} color="var(--sub)" />
          </button>
        </div>

        {/* search */}
        <div style={{ ...fieldBox, marginBottom: 12 }}>
          <FiSearch size={17} color="var(--n-400)" style={{ flexShrink: 0 }} />
          <input autoFocus value={q} onChange={e => setQ(e.target.value)} placeholder="Search customer by name or phone…"
            style={{ border: "none", outline: "none", background: "transparent", fontFamily: "var(--font-body)", fontSize: 14.5, color: "var(--ink)", width: "100%" }} />
        </div>

        {/* walk-in */}
        <button onClick={() => onPick(null)}
          style={{ display: "flex", alignItems: "center", gap: 13, width: "100%", textAlign: "left", padding: "13px 15px", borderRadius: "var(--r-md)", border: "1px dashed var(--n-300)", background: "var(--faint)", cursor: "pointer", marginBottom: 14 }}
          onMouseEnter={e => e.currentTarget.style.borderColor = "var(--primary)"}
          onMouseLeave={e => e.currentTarget.style.borderColor = "var(--n-300)"}
        >
          <div style={{ width: 40, height: 40, borderRadius: 11, background: "var(--n-100)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <FiUser size={19} color="var(--n-600)" />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontFamily: "var(--font-head)", fontSize: 14.5, fontWeight: 700, color: "var(--ink)" }}>Walk-in customer</div>
            <div style={{ fontSize: 12.5, color: "var(--sub)", marginTop: 2 }}>Cash sale · no account</div>
          </div>
          <FiChevronRight size={18} color="var(--n-400)" />
        </button>

        <div style={{ fontSize: 11.5, fontWeight: 700, color: "var(--sub)", letterSpacing: ".04em", textTransform: "uppercase", marginBottom: 8 }}>
          Accounts {q && `· ${matches.length} found`}
        </div>

        {/* customer list */}
        <div style={{ overflowY: "auto", display: "flex", flexDirection: "column", gap: 6, paddingRight: 2, minHeight: 60 }}>
          {matches.length === 0 && <div style={{ textAlign: "center", color: "var(--n-400)", fontSize: 13.5, padding: "24px 0" }}>No customers match "{q}".</div>}
          {matches.map(c => (
            <button key={c.id} onClick={() => onPick(c)}
              style={{ display: "flex", alignItems: "center", gap: 13, width: "100%", textAlign: "left", padding: "11px 14px", borderRadius: "var(--r-md)", border: "1px solid var(--line)", background: "var(--surface)", cursor: "pointer" }}
              onMouseEnter={e => { e.currentTarget.style.background = "var(--blue-50)"; e.currentTarget.style.borderColor = "var(--blue-100)"; }}
              onMouseLeave={e => { e.currentTarget.style.background = "var(--surface)"; e.currentTarget.style.borderColor = "var(--line)"; }}
            >
              <div style={{ width: 40, height: 40, borderRadius: 11, background: "var(--blue-50)", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "var(--font-head)", fontSize: 13.5, fontWeight: 800, color: "var(--primary)", flexShrink: 0 }}>
                {initials(c.name)}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontFamily: "var(--font-head)", fontSize: 14.5, fontWeight: 700, color: "var(--ink)" }}>{c.name}</div>
                <div style={{ fontSize: 12.5, color: "var(--sub)", marginTop: 2 }}>{c.phone || "No phone"}</div>
              </div>
              <div style={{ textAlign: "right", flexShrink: 0 }}>
                <Pill tone={Number(c.balance) > 0 ? "credit" : "cash"}>
                  {Number(c.balance) > 0 ? "Credit" : "Cash"}
                </Pill>
                <div style={{ fontSize: 11.5, color: Number(c.balance) > 0 ? "var(--warning)" : "var(--n-400)", fontWeight: 600, marginTop: 5, fontVariantNumeric: "tabular-nums" }}>
                  {Number(c.balance) > 0 ? `Balance ${MMK(c.balance)} Ks` : "No balance"}
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>
    </Overlay>
  );
}

/* ─── Print invoice ─── */
function doPrint({ saleId, saleDate, customer, pay, lines, products, sub, disc, transportFee, total, approvedBy, location }) {
  const rows = lines.map(l => {
    const p = products.find(x => x.id === l.productId) || {};
    return `<tr>
      <td>${p.name || "—"}<span class="sku">${p.sku || ""}</span></td>
      <td class="r">${l.qty}</td>
      <td class="r">${MMK(l.price)}</td>
      <td class="r">${MMK(l.qty * l.price)}</td>
    </tr>`;
  }).join("");

  const html = `<!doctype html><html><head><meta charset="utf-8"><title>${fmtInvNo(saleId)}</title>
  <style>
    *{margin:0;padding:0;box-sizing:border-box;font-family:-apple-system,Segoe UI,Roboto,sans-serif}
    body{padding:40px 46px;color:#131820}
    .top{display:flex;justify-content:space-between;align-items:flex-start;border-bottom:2px solid #131820;padding-bottom:18px}
    .biz{font-size:22px;font-weight:800;letter-spacing:-.5px}
    .sub{font-size:12px;color:#697583;margin-top:3px}
    .inv{text-align:right}.inv .n{font-size:18px;font-weight:800;color:#1D4ED8}.inv .d{font-size:12px;color:#697583;margin-top:4px}
    .meta{display:flex;gap:48px;margin:22px 0 18px}
    .meta .l{font-size:11px;text-transform:uppercase;letter-spacing:.05em;color:#94A0AE;font-weight:700}
    .meta .v{font-size:14px;font-weight:600;margin-top:4px}
    table{width:100%;border-collapse:collapse;margin-top:6px}
    th{text-align:left;font-size:11px;text-transform:uppercase;letter-spacing:.04em;color:#94A0AE;padding:9px 10px;border-bottom:1px solid #DCE2EA}
    th.r,td.r{text-align:right}
    td{padding:11px 10px;border-bottom:1px solid #EAEEF3;font-size:13.5px}
    td .sku{display:block;font-size:11px;color:#94A0AE;margin-top:2px}
    .totals{margin-left:auto;width:280px;margin-top:18px}
    .totals .row{display:flex;justify-content:space-between;font-size:13.5px;padding:5px 0;color:#4A5563}
    .totals .grand{border-top:2px solid #131820;margin-top:6px;padding-top:10px;font-size:18px;font-weight:800;color:#131820}
    .pill{display:inline-block;font-size:11px;font-weight:700;padding:3px 10px;border-radius:99px;background:#EEF3FF;color:#1D4ED8}
    .foot{margin-top:40px;font-size:11.5px;color:#94A0AE;text-align:center;border-top:1px solid #EAEEF3;padding-top:16px}
  </style></head><body>
  <div class="top">
    <div><div class="biz">Hein Khant Zaw</div><div class="sub">Clothing Wholesale · Yangon</div></div>
    <div class="inv"><div class="n">${fmtInvNo(saleId)}</div><div class="d">${saleDate}</div></div>
  </div>
  <div class="meta">
    <div><div class="l">Billed to</div><div class="v">${customer ? customer.name : "Walk-in customer"}</div></div>
    <div><div class="l">Contact</div><div class="v">${customer ? (customer.phone || "—") : "—"}</div></div>
    <div><div class="l">Payment</div><div class="v"><span class="pill">${pay === "cash" ? "Cash" : "Credit"}</span></div></div>
    ${location?.name ? `<div><div class="l">Location</div><div class="v">${location.name}</div></div>` : ""}
  </div>
  <table><thead><tr><th>Product</th><th class="r">Qty</th><th class="r">Unit (MMK)</th><th class="r">Amount</th></tr></thead>
  <tbody>${rows}</tbody></table>
  <div class="totals">
    <div class="row"><span>Subtotal</span><span>${MMK(sub)} Ks</span></div>
    ${disc > 0 ? `<div class="row"><span>Discount${approvedBy ? ` (appr. ${approvedBy})` : ""}</span><span>– ${MMK(disc)} Ks</span></div>` : ""}
    ${transportFee > 0 ? `<div class="row"><span>Transportation</span><span>+ ${MMK(transportFee)} Ks</span></div>` : ""}
    <div class="row grand"><span>Total</span><span>${MMK(total)} MMK</span></div>
  </div>
  <div class="foot">Thank you for your business · Goods sold are checked on delivery · ${fmtInvNo(saleId)}</div>
  <div class="no-print" style="margin-top:32px;text-align:center">
    <button onclick="window.close()" style="padding:10px 28px;font-size:14px;border:1px solid #ddd;border-radius:8px;background:#f4f6f9;cursor:pointer">✕ Close without printing</button>
  </div>
  <style>@media print{.no-print{display:none}}</style>
  </body></html>`;

  const w = window.open("", "_blank", "width=720,height=900");
  if (!w) { alert("Please allow pop-ups to print the invoice."); return; }
  w.document.open(); w.document.write(html); w.document.close();
  w.focus();
  setTimeout(() => {
    try { w.print(); } catch (e) {}
    w.onafterprint = () => w.close();
  }, 350);
}

/* ─── Main Sale screen ─── */
export default function Sales({ token, go, location }) {
  const [products, setProducts]     = useState([]);
  const [customers, setCustomers]   = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading]       = useState(true);

  // step 1
  const [started, setStarted]   = useState(false);
  const [customer, setCustomer] = useState(null); // null = walk-in

  // invoice state
  const [saleDate, setSaleDate] = useState(today());
  const [pay, setPay]           = useState("cash");
  const [lines, setLines]       = useState([]);
  const [discMode, setDiscMode] = useState("pct");
  const [discInput, setDiscInput] = useState("");
  const [transportInput, setTransportInput] = useState("0");
  const [approvedBy, setApprovedBy]     = useState(null);
  const [approvedById, setApprovedById] = useState(null);

  // UI state
  const [prodQuery, setProdQuery] = useState("");
  const [prodCat, setProdCat]     = useState("all");
  const [prodPage, setProdPage]   = useState(1);
  const [custOpen, setCustOpen]   = useState(false);
  const [custQuery, setCustQuery] = useState("");
  const [pinOpen, setPinOpen]     = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError]           = useState("");
  const [success, setSuccess]       = useState(null);

  useEffect(() => {
    const qs = location?.id ? `?location_id=${location.id}` : "";
    Promise.all([
      apiGet(`/api/inventory/products/sale${qs}`, token).then(r => r.json()),
      apiGet("/api/inventory/categories", token).then(r => r.json()),
    ])
      .then(([d, cats]) => {
        setProducts(Array.isArray(d.products) ? d.products : []);
        setCustomers(Array.isArray(d.customers) ? d.customers : []);
        setCategories(Array.isArray(cats) ? cats : []);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [token, location?.id]);

  /* ── computed ── */
  const sub = lines.reduce((s, l) => s + l.qty * l.price, 0);
  const wantDisc = (() => {
    const v = parseFloat(discInput) || 0;
    if (v <= 0) return 0;
    return discMode === "pct"
      ? Math.round(sub * Math.min(v, 100) / 100)
      : Math.min(Math.round(v), sub);
  })();
  const disc  = approvedBy ? wantDisc : 0;
  const transportFee = Math.max(0, Math.round(parseFloat(transportInput) || 0));
  const total = sub - disc + transportFee;

  /* ── product grid helpers ── */
  const stockOk = (p) => p.quantity > 0;

  const addProduct = useCallback((p) => {
    setLines(ls => {
      const existing = ls.find(l => l.productId === p.id);
      if (existing) return ls.map(l => l.productId === p.id ? { ...l, qty: Math.min(l.qty + 1, p.quantity) } : l);
      return [...ls, { productId: p.id, qty: 1, price: Number(p.price), maxQty: p.quantity }];
    });
    setError("");
  }, []);

  const bumpQty = (id, d) => setLines(ls => ls.map(l => {
    if (l.productId !== id) return l;
    return { ...l, qty: Math.max(1, Math.min(l.qty + d, l.maxQty)) };
  }));

  const setQty = (id, raw) => {
    const n = Math.max(1, Math.min(parseInt(numOnly(raw) || "1", 10) || 1, lines.find(l => l.productId === id)?.maxQty || 1));
    setLines(ls => ls.map(l => l.productId === id ? { ...l, qty: n } : l));
  };

  const setLinePrice = (id, raw) => {
    const n = Math.max(0, parseInt(numOnly(raw) || "0", 10) || 0);
    setLines(ls => ls.map(l => l.productId === id ? { ...l, price: n } : l));
  };

  const removeLine = (id) => setLines(ls => ls.filter(l => l.productId !== id));

  const onDisc     = (v) => { setDiscInput(numOnly(v)); setApprovedBy(null); setApprovedById(null); };
  const onDiscMode = (m) => { setDiscMode(m); setApprovedBy(null); setApprovedById(null); };

  const pq = prodQuery.trim().toLowerCase();
  const gridProducts = products.filter(p =>
    stockOk(p) &&
    (prodCat === "all" || String(p.category) === prodCat) &&
    (!pq || p.name.toLowerCase().includes(pq) || (p.sku || "").toLowerCase().includes(pq))
  );
  const gridPage = gridProducts.slice((prodPage - 1) * PER_PAGE_DEFAULT, prodPage * PER_PAGE_DEFAULT);

  const custMatches = customers.filter(c => {
    const s = custQuery.trim().toLowerCase();
    return !s || c.name.toLowerCase().includes(s) || (c.phone || "").replace(/\s/g, "").includes(s.replace(/\s/g, ""));
  });

  /* ── submit ── */
  const handleComplete = async () => {
    if (lines.length === 0) return setError("Add at least one product.");
    if (pay === "credit" && !customer) return setError("Credit sales require a customer.");
    if (!location?.id) return setError("No working location selected — choose one at the top of the screen.");
    if (wantDisc > 0 && !approvedBy) { setPinOpen(true); return; }
    setSubmitting(true); setError("");
    try {
      const body = {
        employee_token: token,
        is_credit: pay === "credit",
        sale_date: saleDate,
        location_id: location.id,
        discount_percentage: discMode === "pct" ? (parseFloat(discInput) || 0) : 0,
        discount_amount: discMode === "amt" ? (parseFloat(discInput) || 0) : 0,
        transportation_fee: transportFee,
        products: lines.map(l => ({ product_id: l.productId, quantity: l.qty, unit_price: l.price })),
      };
      if (customer) body.customer_id = customer.id;
      if (approvedById) body.discount_approved_by_id = approvedById;

      const res = await apiPost("/api/inventory/products/sale", body, token);
      if (res.ok) {
        const data = await res.json();
        setSuccess({ saleId: data.sale_id, total, pay, itemCount: lines.length, location });
      } else {
        const err = await res.json().catch(() => ({}));
        setError(err.error || "Failed to complete sale.");
      }
    } catch { setError("Cannot connect to server."); }
    finally { setSubmitting(false); }
  };

  const resetSale = () => {
    setStarted(false); setCustomer(null); setLines([]);
    setDiscInput(""); setApprovedBy(null); setApprovedById(null);
    setTransportInput("0");
    setPay("cash"); setSaleDate(today()); setError(""); setSuccess(null);
  };

  if (loading) return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 300 }}>
      <span style={{ color: "var(--sub)", fontSize: 14 }}>Loading…</span>
    </div>
  );

  return (
    <div style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>

      {/* ── LEFT: product search + grid ── */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontFamily: "var(--font-head)", fontSize: 27, fontWeight: 800, letterSpacing: "-.025em", color: "var(--ink)", marginBottom: 4 }}>
          New Sale <span style={{ fontFamily: "var(--font-mm)", fontSize: 16, fontWeight: 500, color: "var(--n-400)" }}>အရောင်း</span>
        </div>
        <div style={{ fontSize: 14, color: "var(--sub)", marginBottom: 18 }}>Search by name or SKU, filter by category, or tap a product below</div>

        {/* product search */}
        <div style={{ marginBottom: 12 }}>
          <div style={{ ...fieldBox, padding: "11px 14px" }}>
            <FiSearch size={18} color="var(--n-400)" style={{ flexShrink: 0 }} />
            <input value={prodQuery} onChange={e => { setProdQuery(e.target.value); setProdPage(1); }}
              placeholder="Search product name or SKU…"
              style={{ border: "none", outline: "none", background: "transparent", fontFamily: "var(--font-body)", fontSize: 14.5, color: "var(--ink)", width: "100%" }} />
            {prodQuery && <button onClick={() => { setProdQuery(""); setProdPage(1); }} style={{ border: "none", background: "transparent", cursor: "pointer" }}><FiX size={15} color="var(--n-400)" /></button>}
          </div>
        </div>

        {/* category filter chips */}
        <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
          {[{ id: "all", name: "All" }, ...categories].map(c => {
            const on = prodCat === String(c.id);
            return (
              <button key={c.id} onClick={() => { setProdCat(String(c.id)); setProdPage(1); }}
                style={{ padding: "7px 14px", borderRadius: 999, cursor: "pointer", border: `1px solid ${on ? "var(--primary)" : "var(--line)"}`, background: on ? "var(--primary)" : "var(--surface)", color: on ? "#fff" : "var(--n-600)", fontFamily: "var(--font-body)", fontSize: 13, fontWeight: 600 }}>
                {c.name}
              </button>
            );
          })}
        </div>

        {/* product grid */}
        {gridProducts.length === 0 && (
          <div style={{ textAlign: "center", color: "var(--n-400)", fontSize: 13.5, padding: "40px 0" }}>
            No in-stock products match{prodQuery ? ` "${prodQuery}"` : " this filter"}.
          </div>
        )}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 12 }}>
          {gridPage.map(p => {
            const inCart = lines.find(l => l.productId === p.id);
            return (
              <button key={p.id} onClick={() => addProduct(p)}
                style={{ display: "flex", flexDirection: "column", textAlign: "left", background: "var(--surface)", border: `1px solid ${inCart ? "var(--primary)" : "var(--line)"}`, borderRadius: "var(--r-md)", padding: "14px 16px", cursor: "pointer", boxShadow: "var(--shadow-sm)" }}
                onMouseEnter={e => { if (!inCart) e.currentTarget.style.borderColor = "var(--blue-100)"; }}
                onMouseLeave={e => { if (!inCart) e.currentTarget.style.borderColor = "var(--line)"; }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
                  <div style={{ fontFamily: "var(--font-head)", fontSize: 14, fontWeight: 700, color: "var(--ink)", lineHeight: 1.25, minHeight: 34, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{p.name}</div>
                  <span style={{ width: 23, height: 23, borderRadius: 7, background: inCart ? "var(--primary)" : "var(--blue-50)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    {inCart ? <FiCheck size={14} color="#fff" /> : <FiPlus size={14} color="var(--primary)" />}
                  </span>
                </div>
                <div style={{ fontSize: 11.5, color: "var(--sub)", marginTop: 5, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {p.category_name || "—"} · {p.quantity} stk
                </div>
                <div style={{ fontFamily: "var(--font-head)", fontSize: 15.5, fontWeight: 800, color: "var(--primary)", marginTop: 9, fontVariantNumeric: "tabular-nums" }}>
                  {MMK(p.price)} <span style={{ fontSize: 10.5, color: "var(--n-400)", fontWeight: 600 }}>MMK</span>
                </div>
              </button>
            );
          })}
        </div>
        <Pagination total={gridProducts.length} page={prodPage} onChange={setProdPage} />
      </div>

      {/* ── RIGHT: invoice panel ── */}
      <div style={{ width: 412, flexShrink: 0, background: "var(--surface)", border: "1px solid var(--line)", borderRadius: "var(--r-lg)", boxShadow: "var(--shadow-sm)", display: "flex", flexDirection: "column", position: "sticky", top: 0, maxHeight: "calc(100vh - 84px)", overflow: "hidden" }}>
        {/* invoice header */}
        <div style={{ padding: "16px 20px", borderBottom: "1px solid var(--hair)", display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0 }}>
          <div style={{ fontFamily: "var(--font-head)", fontSize: 17, fontWeight: 800, color: "var(--ink)" }}>Invoice</div>
          <span style={{ fontSize: 12.5, color: "var(--sub)" }}>Draft</span>
        </div>

        {/* scrollable body */}
        <div style={{ flex: 1, overflowY: "auto", minHeight: 0 }}>

          {/* sale date */}
          <div style={{ padding: "14px 20px 4px" }}>
            <label style={lbl}>Sale date</label>
            <div style={fieldBox}>
              <FiCalendar size={16} color="var(--n-400)" style={{ flexShrink: 0 }} />
              <input type="date" value={saleDate} onChange={e => setSaleDate(e.target.value)}
                style={{ border: "none", outline: "none", background: "transparent", fontFamily: "var(--font-body)", fontSize: 13.5, color: "var(--ink)", width: "100%" }} />
            </div>
          </div>

          {/* payment type */}
          <div style={{ padding: "10px 20px 4px" }}>
            <label style={lbl}>Payment type</label>
            <div style={{ display: "flex", gap: 8 }}>
              {[["cash","Cash"],["credit","Credit"]].map(([m, t]) => (
                <button key={m} onClick={() => { setPay(m); setError(""); }}
                  style={{ flex: 1, padding: "9px", borderRadius: "var(--r-sm)", cursor: "pointer", border: `1px solid ${pay === m ? "var(--primary)" : "var(--line)"}`, background: pay === m ? "var(--blue-50)" : "var(--surface)", color: pay === m ? "var(--primary)" : "var(--n-600)", fontFamily: "var(--font-head)", fontWeight: 700, fontSize: 13.5 }}>
                  {t}
                </button>
              ))}
            </div>
          </div>

          {/* customer field */}
          <div style={{ padding: "10px 20px 6px", position: "relative" }}>
            <label style={lbl}>Customer {pay === "credit" && <span style={{ color: "var(--danger)" }}>· required</span>}</label>
            {customer && !custOpen ? (
              <div style={{ display: "flex", alignItems: "center", gap: 11, border: "1px solid var(--line)", borderRadius: "var(--r-sm)", padding: "9px 12px", background: "var(--surface)" }}>
                <div style={{ width: 32, height: 32, borderRadius: 9, background: "var(--blue-50)", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "var(--font-head)", fontSize: 12, fontWeight: 800, color: "var(--primary)", flexShrink: 0 }}>{initials(customer.name)}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13.5, fontWeight: 700, color: "var(--ink)" }}>{customer.name}</div>
                  <div style={{ fontSize: 11.5, color: "var(--sub)" }}>Balance {Number(customer.balance) > 0 ? MMK(customer.balance) + " Ks" : "0"}</div>
                </div>
                <button onClick={() => { setCustOpen(true); setCustQuery(""); }} style={{ border: "none", background: "transparent", cursor: "pointer", padding: 5 }}><FiEdit2 size={14} color="var(--n-400)" /></button>
                <button onClick={() => setCustomer(null)} style={{ border: "none", background: "transparent", cursor: "pointer", padding: 5 }}><FiX size={14} color="var(--n-400)" /></button>
              </div>
            ) : (
              <div style={fieldBox}>
                <FiSearch size={16} color="var(--n-400)" style={{ flexShrink: 0 }} />
                <input autoFocus={custOpen} value={custQuery} onChange={e => { setCustQuery(e.target.value); setCustOpen(true); }} onFocus={() => setCustOpen(true)}
                  placeholder="Search name or phone…"
                  style={{ border: "none", outline: "none", background: "transparent", fontFamily: "var(--font-body)", fontSize: 13.5, color: "var(--ink)", width: "100%" }} />
              </div>
            )}
            {custOpen && (
              <div style={{ position: "absolute", top: "calc(100% + 4px)", left: 20, right: 20, background: "var(--surface)", border: "1px solid var(--line)", borderRadius: "var(--r-md)", boxShadow: "var(--shadow-md)", zIndex: 25, overflow: "hidden", maxHeight: 240, overflowY: "auto" }}>
                <button onClick={() => { setCustomer(null); setCustOpen(false); }}
                  style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, padding: "10px 13px", border: "none", borderBottom: "1px solid var(--hair)", background: "transparent", cursor: "pointer", textAlign: "left" }}
                  onMouseEnter={e => e.currentTarget.style.background = "var(--faint)"}
                  onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                >
                  <div style={{ fontSize: 13.5, fontWeight: 700, color: "var(--ink)" }}>Walk-in customer</div>
                  <span style={{ fontSize: 12, color: "var(--sub)" }}>No account</span>
                </button>
                {custMatches.map(c => (
                  <button key={c.id} onClick={() => { setCustomer(c); setCustOpen(false); if (Number(c.balance) > 0) setPay("credit"); setError(""); }}
                    style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, padding: "10px 13px", border: "none", borderBottom: "1px solid var(--hair)", background: "transparent", cursor: "pointer", textAlign: "left" }}
                    onMouseEnter={e => e.currentTarget.style.background = "var(--faint)"}
                    onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                  >
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 13.5, fontWeight: 700, color: "var(--ink)" }}>{c.name}</div>
                      <div style={{ fontSize: 11.5, color: "var(--sub)" }}>{c.phone || "No phone"}</div>
                    </div>
                    <span style={{ fontSize: 12, fontWeight: 700, color: Number(c.balance) > 0 ? "var(--warning)" : "var(--n-400)", fontVariantNumeric: "tabular-nums" }}>
                      {Number(c.balance) > 0 ? MMK(c.balance) : "0"}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* line items */}
          <div style={{ padding: "10px 16px 6px" }}>
            <label style={{ ...lbl, paddingLeft: 4 }}>Items</label>
            {lines.length === 0 && (
              <div style={{ textAlign: "center", color: "var(--n-400)", fontSize: 13, padding: "26px 0" }}>No items yet — search or tap a product</div>
            )}
            {lines.map(l => {
              const p = products.find(x => x.id === l.productId) || {};
              return (
                <div key={l.productId} style={{ padding: "10px 8px", borderBottom: "1px solid var(--hair)" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13.5, fontWeight: 700, color: "var(--ink)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{p.name}</div>
                      <div style={{ fontSize: 11, color: "var(--sub)", marginTop: 1 }}>{p.sku} · max {l.maxQty}</div>
                    </div>
                    <button onClick={() => removeLine(l.productId)} style={{ border: "none", background: "transparent", cursor: "pointer", padding: 4 }}>
                      <FiTrash2 size={14} color="var(--n-400)" />
                    </button>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                      <button onClick={() => bumpQty(l.productId, -1)} style={sqbtn}><FiMinus size={13} color="var(--n-600)" /></button>
                      <input value={l.qty} onChange={e => setQty(l.productId, e.target.value)}
                        style={{ width: 42, height: 30, textAlign: "center", border: "1px solid var(--line)", borderRadius: 7, fontFamily: "var(--font-head)", fontSize: 13.5, fontWeight: 700, color: "var(--ink)", fontVariantNumeric: "tabular-nums", outline: "none" }} />
                      <button onClick={() => bumpQty(l.productId, 1)} style={sqbtn}><FiPlus size={13} color="var(--n-600)" /></button>
                    </div>
                    <span style={{ fontSize: 12, color: "var(--n-400)" }}>×</span>
                    <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 4, padding: "0 8px" }}>
                      <input value={l.price} onChange={e => setLinePrice(l.productId, e.target.value)}
                        style={{ width: 76, height: 30, textAlign: "right", border: "1px solid var(--line)", borderRadius: 7, padding: "0 8px", fontFamily: "var(--font-body)", fontSize: 13, fontWeight: 600, color: "var(--ink)", fontVariantNumeric: "tabular-nums", outline: "none" }} />
                      <span style={{ fontSize: 11, color: "var(--n-400)", fontWeight: 600 }}>Ks</span>
                    </div>
                    <div style={{ width: 90, textAlign: "right", fontFamily: "var(--font-head)", fontSize: 13.5, fontWeight: 800, color: "var(--ink)", fontVariantNumeric: "tabular-nums" }}>
                      {MMK(l.qty * l.price)}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* discount */}
          <div style={{ padding: "10px 20px 16px" }}>
            <label style={lbl}>Discount</label>
            <div style={{ display: "flex", gap: 8, alignItems: "stretch" }}>
              <div style={{ display: "flex", border: "1px solid var(--line)", borderRadius: "var(--r-sm)", overflow: "hidden" }}>
                {[["pct","%"],["amt","Ks"]].map(([m, t]) => (
                  <button key={m} onClick={() => onDiscMode(m)}
                    style={{ padding: "0 12px", cursor: "pointer", border: "none", background: discMode === m ? "var(--primary)" : "var(--surface)", color: discMode === m ? "#fff" : "var(--n-500)", fontFamily: "var(--font-head)", fontWeight: 700, fontSize: 13 }}>
                    {t}
                  </button>
                ))}
              </div>
              <div style={{ ...fieldBox, flex: 1 }}>
                <input value={discInput} onChange={e => onDisc(e.target.value)} placeholder="0"
                  style={{ border: "none", outline: "none", background: "transparent", fontFamily: "var(--font-body)", fontSize: 13.5, color: "var(--ink)", width: "100%", fontVariantNumeric: "tabular-nums" }} />
                <span style={{ fontSize: 12, color: "var(--n-400)", fontWeight: 600 }}>{discMode === "pct" ? "percent" : "MMK"}</span>
              </div>
            </div>

            {wantDisc > 0 && !approvedBy && (
              <div style={{ display: "flex", alignItems: "center", gap: 9, marginTop: 9, padding: "9px 12px", background: "var(--warning-bg)", borderRadius: "var(--r-sm)" }}>
                <FiLock size={14} color="var(--warning)" style={{ flexShrink: 0 }} />
                <span style={{ fontSize: 12.5, color: "var(--warning)", fontWeight: 600, flex: 1 }}>Needs manager approval ({MMK(wantDisc)} Ks)</span>
                <button onClick={() => setPinOpen(true)} style={{ border: "none", background: "var(--warning)", color: "#fff", borderRadius: 7, padding: "5px 11px", fontFamily: "var(--font-head)", fontWeight: 700, fontSize: 12.5, cursor: "pointer" }}>Approve</button>
              </div>
            )}
            {wantDisc > 0 && approvedBy && (
              <div style={{ display: "flex", alignItems: "center", gap: 9, marginTop: 9, padding: "9px 12px", background: "var(--success-bg)", borderRadius: "var(--r-sm)" }}>
                <FiCheck size={14} color="var(--success)" />
                <span style={{ fontSize: 12.5, color: "var(--success)", fontWeight: 600 }}>Approved by {approvedBy}</span>
              </div>
            )}
          </div>

          {/* transportation fee */}
          <div style={{ padding: "0 20px 16px" }}>
            <label style={lbl}>Transportation fee <span style={{ fontWeight: 500, color: "var(--n-400)" }}>(optional)</span></label>
            <div style={fieldBox}>
              <FiTruck size={16} color="var(--n-400)" style={{ flexShrink: 0 }} />
              <input value={transportInput} onChange={e => setTransportInput(numOnly(e.target.value))} placeholder="0"
                style={{ border: "none", outline: "none", background: "transparent", fontFamily: "var(--font-body)", fontSize: 13.5, color: "var(--ink)", width: "100%", fontVariantNumeric: "tabular-nums" }} />
              <span style={{ fontSize: 12, color: "var(--n-400)", fontWeight: 600 }}>MMK</span>
            </div>
          </div>
        </div>

        {/* footer: totals + complete */}
        <div style={{ padding: "14px 20px 18px", borderTop: "1px solid var(--hair)", background: "var(--faint)", borderRadius: "0 0 var(--r-lg) var(--r-lg)", flexShrink: 0 }}>
          <SaleRow k="Subtotal" v={MMK(sub) + " Ks"} />
          {disc > 0 && <SaleRow k="Discount" v={"– " + MMK(disc) + " Ks"} accent />}
          {transportFee > 0 && <SaleRow k="Transportation" v={"+ " + MMK(transportFee) + " Ks"} />}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginTop: 8, paddingTop: 11, borderTop: "1px solid var(--line)" }}>
            <span style={{ fontFamily: "var(--font-head)", fontSize: 15, fontWeight: 700, color: "var(--ink)" }}>Total</span>
            <span style={{ fontFamily: "var(--font-head)", fontSize: 24, fontWeight: 800, color: "var(--ink)", fontVariantNumeric: "tabular-nums" }}>
              {MMK(total)} <span style={{ fontSize: 13, color: "var(--sub)" }}>MMK</span>
            </span>
          </div>
          {error && (
            <div style={{ display: "flex", alignItems: "center", gap: 7, marginTop: 11, color: "var(--danger)", fontSize: 12.5, fontWeight: 600 }}>
              <FiAlertTriangle size={14} /> {error}
            </div>
          )}
          <Btn kind="acc" onClick={handleComplete} disabled={submitting || lines.length === 0}
            style={{ width: "100%", justifyContent: "center", height: 54, marginTop: 13, fontSize: 16 }}>
            <FiCheck size={18} /> {submitting ? "Processing…" : "Complete Sale"}
          </Btn>
        </div>
      </div>

      {/* ── modals ── */}
      {!started && (
        <CustomerStart
          customers={customers}
          onPick={(c) => { setCustomer(c); if (c && Number(c.balance) > 0) setPay("credit"); setStarted(true); setError(""); }}
          onCancel={() => go("overview")}
        />
      )}

      {pinOpen && (
        <PinModal
          token={token}
          onClose={() => setPinOpen(false)}
          onVerified={(name, id) => { setApprovedBy(name); setApprovedById(id); setPinOpen(false); }}
        />
      )}

      {success && (
        <SuccessModal
          {...success}
          go={go}
          onPrint={() => doPrint({ saleId: success.saleId, saleDate, customer, pay, lines, products, sub, disc, transportFee, total, approvedBy, location })}
          onNew={resetSale}
        />
      )}
    </div>
  );
}
